import * as React from "react"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import ProgressBar from "../ProgressBar"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import { theme } from "../../theme"
import { useSharedPlanTerminalsById } from "../../../hooks/useSharedPlanTerminalsById"
import { formatSharedPlanTerminalsToTerminalDataModel } from "../../../utils/formatters"
import { getPercentage, isEvenNumber, mbGbDisplay } from "../../../utils/helpers"
import TerminalStatusCircle from "../../atoms/TerminalStatusCircle"
import H3 from "../../atoms/typography/H3"
import TextButton from "../../atoms/buttons/TextButton"
import { ArrowDropDownIcon } from "../../atoms/icons/ArrowDropDownIcon"
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrows"
import { ArrowDropUpIcon } from "../../atoms/icons/ArrowDropUpIcon"
import UsageStatisticsSection from "../../organisms/charts/UsageStatisticsSection"
import { MediaQuery, TERMINAL_MAX_WARNING, getTerminalGroupsText } from "../../../utils/design-helpers"
import { TerminalDataModel } from "../../../utils/models"
import BodyMedium from "../../atoms/typography/BodyMedium"
import Body from "../../atoms/typography/Body"
import { SlideOutHeight } from "../../atoms/animations/SlideOutHeight"
import dayjs from "dayjs"
import { captureExceptionWithContext } from "../../../utils/sentry"

interface Props {
  apiPlanId: number
}

const SortingCol = styled(Col)`
  display: flex;
  justify-content: center;
`

const BarContentContainer = styled.div<{ greybg?: boolean }>`
  background-color: ${(props) => (props.greybg ? props.theme.colors.greyLighter : props.theme.colors.white)};
  padding: ${(props) => props.theme.spacings.large};

  :hover .poop {
    display: block;
  }
`

const ProductImage = styled.img`
  width: 35px;
  height: 35px;
  object-fit: cover;

  ${MediaQuery.XS} {
    width: 66px;
    height: 72px;
  }
`

const MobileTerminalContainer = styled.div<{ open?: boolean }>`
  padding: 1rem;
  background-color: ${(props) => (props.open ? props.theme.colors.greyLight : undefined)};
  border-radius: ${(props) => props.theme.borders.softRadius};
  margin: 4px;
`

const MobileTerminalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SharedDataPlanTerminalsOverview: React.FunctionComponent<Props> = ({ apiPlanId }) => {
  const { t } = useTranslation()
  const [sortAscDesc, setSortAscDesc] = React.useState(false)
  const [sorting, setSorting] = React.useState("naz")
  const onSortTerminalName = () => {
    sortAscDesc ? setSorting("naz") : setSorting("nza")
    setSortAscDesc(!sortAscDesc)
  }
  const onSortSerialNumber = () => {
    sortAscDesc ? setSorting("snhl") : setSorting("snlh")
    setSortAscDesc(!sortAscDesc)
  }
  const onSortDataUsage = () => {
    sortAscDesc ? setSorting("duhl") : setSorting("dulh")
    setSortAscDesc(!sortAscDesc)
  }
  const onSortGroups = () => {
    sortAscDesc ? setSorting("ghl") : setSorting("glh")
    setSortAscDesc(!sortAscDesc)
  }

  const { sharedPlanTerminals, sharedPlanTerminalsError, sharedPlanTerminalsLoading } =
    useSharedPlanTerminalsById(apiPlanId)
  const formatted = formatSharedPlanTerminalsToTerminalDataModel(
    sharedPlanTerminals?.sharedInstances,
    "",
    "",
    sorting,
    dayjs(sharedPlanTerminals?.start).format("YYYY-MM-DDTHH:mm:ssZ").toString(),
  )

  if (sharedPlanTerminalsError) {
    captureExceptionWithContext(sharedPlanTerminalsError, "Error fetching shared plan terminals")
  }

  const [terminalsOpen, setTerminalsOpen] = React.useState<Array<string>>([])
  //Handle open sections
  const checkCardIsOpen = (id: string) => {
    return terminalsOpen.includes(id)
  }
  const onClickOpenTerminalDetails = (id: string) => {
    if (checkCardIsOpen(id)) {
      const arr = terminalsOpen.filter((x) => x !== id)
      setTerminalsOpen(arr)
    } else {
      setTerminalsOpen([...terminalsOpen, id])
    }
  }
  const allTerminalIds: Array<string> = []
  formatted?.map((t: any) => allTerminalIds.push(t.id))

  return (
    <div>
      <Hidden xs>
        <Row justify="center">
          <SortingCol md={2}>
            <TextButton
              title="Name"
              icon={<SortingArrowsIcon color={theme.colors.greyDark} />}
              onClick={onSortTerminalName}
            />
          </SortingCol>
          <SortingCol md={2}>
            <TextButton
              title="Serial Number"
              icon={<SortingArrowsIcon color={theme.colors.greyDark} />}
              onClick={onSortSerialNumber}
            />
          </SortingCol>
          <SortingCol md={2}>
            <TextButton
              title="Group"
              icon={<SortingArrowsIcon color={theme.colors.greyDark} />}
              onClick={onSortGroups}
            />
          </SortingCol>
          <SortingCol md={2}>
            <TextButton title="Status" icon={<SortingArrowsIcon color={theme.colors.greyDark} />} />
          </SortingCol>
          <SortingCol md={2}>
            <TextButton
              title="Data usage"
              icon={<SortingArrowsIcon color={theme.colors.greyDark} />}
              onClick={onSortDataUsage}
            />
          </SortingCol>
          <SortingCol md={2} />
        </Row>
      </Hidden>
      {sharedPlanTerminalsLoading ? (
        <LoadingSpinner />
      ) : (
        <div>
          <Visible xs>
            {formatted?.map((terminal: TerminalDataModel, index: number) => {
              return (
                <MobileTerminalContainer
                  key={terminal.id}
                  onClick={() => onClickOpenTerminalDetails(terminal.id.toString())}
                  open={checkCardIsOpen(terminal.id.toString())}
                >
                  <MobileTerminalRow>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <ProductImage src={terminal.imgSrc} />
                      <Spacer spacing={0.5} />
                      <div>
                        <BodyMedium>{terminal.name}</BodyMedium>
                        <Body>{terminal.serialNumber}</Body>
                      </div>
                    </div>
                    <div>{checkCardIsOpen(terminal.id.toString()) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}</div>
                  </MobileTerminalRow>
                  <SlideOutHeight isVisible={checkCardIsOpen(terminal.id.toString())}>
                    <HorizontalSpacer spacing={4} />
                    <Body>Group:</Body>
                    <HorizontalSpacer spacing={1} />
                    <BodyMedium>{getTerminalGroupsText(terminal?.groups ?? [])}</BodyMedium>
                    <HorizontalSpacer spacing={4} />
                    <Body>Status:</Body>
                    <HorizontalSpacer spacing={1} />
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <TerminalStatusCircle status={terminal.status} />
                      <Spacer spacing={0.5} />
                      <BodyMedium>{terminal.status}</BodyMedium>
                    </div>
                    <HorizontalSpacer spacing={4} />
                    <Body>Data usage:</Body>
                    <HorizontalSpacer spacing={1} />
                    <Row align="center">
                      <Col xs={6}>
                        <ProgressBar
                          warningMax={TERMINAL_MAX_WARNING}
                          currentProgress={getPercentage(terminal.dataUsage?.used ?? 0, terminal.dataUsage?.total ?? 0)}
                          border
                        />
                      </Col>
                      <Col xs={6}>
                        <Body textAlign="center">
                          {mbGbDisplay(terminal.dataUsage?.used ?? 0)} / {mbGbDisplay(terminal.dataUsage?.total ?? 0)}
                        </Body>
                      </Col>
                    </Row>
                  </SlideOutHeight>
                </MobileTerminalContainer>
              )
            })}
          </Visible>
          <Hidden xs>
            {formatted?.map((terminal: TerminalDataModel, index: number) => (
              <div key={terminal.id}>
                <BarContentContainer greybg={isEvenNumber(index)}>
                  <Row align="center" onClick={() => onClickOpenTerminalDetails(terminal.id.toString())}>
                    <Col md={2}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ProductImage src={terminal.imgSrc} />
                        <Spacer spacing={0.5} />
                        <H3>{terminal.name}</H3>
                      </div>
                    </Col>
                    <SortingCol md={2}>
                      <H3 textAlign="center">{terminal.serialNumber}</H3>
                    </SortingCol>
                    <SortingCol md={2}>
                      <H3 textAlign="center">{getTerminalGroupsText(terminal.groups ?? [])}</H3>
                    </SortingCol>
                    <SortingCol md={2}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TerminalStatusCircle status={terminal.status} />
                        <Spacer spacing={0.5} />
                        <H3>{terminal.status}</H3>
                      </div>
                    </SortingCol>
                    <SortingCol md={2}>
                      <div style={{ width: "100%" }}>
                        <ProgressBar
                          warningMax={TERMINAL_MAX_WARNING}
                          currentProgress={getPercentage(terminal.dataUsage?.used ?? 0, terminal.dataUsage?.total ?? 0)}
                          border
                        />
                        <H3 textAlign="center">
                          {mbGbDisplay(terminal.dataUsage?.used ?? 0)} / {mbGbDisplay(terminal.dataUsage?.total ?? 0)}
                        </H3>
                      </div>
                    </SortingCol>
                    <SortingCol md={2}>
                      <TextButton
                        title={t("details")}
                        icon={checkCardIsOpen(terminal.id.toString()) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                        onClick={() => onClickOpenTerminalDetails(terminal.id.toString())}
                      />
                    </SortingCol>
                  </Row>
                </BarContentContainer>
                {checkCardIsOpen(terminal.id.toString()) && (
                  <UsageStatisticsSection
                    isOpen={checkCardIsOpen(terminal.id.toString())}
                    id={terminal.id}
                    name={terminal.name}
                    detailsData={terminal}
                  />
                )}
              </div>
            ))}
          </Hidden>
        </div>
      )}
      <HorizontalSpacer spacing={2} />
    </div>
  )
}

export default SharedDataPlanTerminalsOverview
